import {
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { SimpleChanges } from '@angular/core';
import { FilterList, StudyFilterService } from '@services';
import { Subject, takeUntil } from 'rxjs';

interface FilterColumn {
  key: string;
  value: string[];
}

@Component({
  selector: 'filter-menu',
  templateUrl: './filter-menu.component.html',
  styleUrl: './filter-menu.component.scss'
})
export class FilterMenuComponent implements OnInit, OnChanges, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly studyFilterService = inject(StudyFilterService);

  showMenu = false;

  @Input() columnTitleMappings: Record<string, string> = {};
  @Input() variant: 'default' | 'kpi-expanded' = 'default';
  @Input() label = 'Filters applied';
  @Input() useExternalState = false;
  @Input() externalFiltersApplied: Record<string, string[]> = {};
  @Input() externalTotalFiltersApplied = 0;

  @Output() toggleMenuEvent = new EventEmitter<{ event: MouseEvent; column: string }>();
  @Output() closeMenuEvent = new EventEmitter<void>();
  @Output() externalRemoveFilter = new EventEmitter<{ field: string; value: string }>();
  @Output() externalClearAll = new EventEmitter<void>();

  private _totalFiltersApplied = 0;
  private _filtersAppliedArray: FilterColumn[] = [];

  ngOnInit(): void {
    if (this.useExternalState) {
      this.refreshFromExternalState();
      return;
    }

    this.refreshFromService();
    this.studyFilterService.filterState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshFromService());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.useExternalState) return;

    if (
      changes['useExternalState'] ||
      changes['externalFiltersApplied'] ||
      changes['externalTotalFiltersApplied']
    ) {
      this.refreshFromExternalState();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get filtersApplied(): FilterList {
    return this.studyFilterService.getCurrentState().appliedFilters;
  }

  get count(): number {
    return this._totalFiltersApplied;
  }

  get showBadge(): boolean {
    return this.variant === 'default' || this._totalFiltersApplied > 0;
  }

  get totalFiltersApplied(): number {
    return this._totalFiltersApplied;
  }

  get filtersAppliedArray(): FilterColumn[] {
    return this._filtersAppliedArray;
  }

  onToggle(event: MouseEvent): void {
    this.toggleMenu(event);
  }

  toggleMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.showMenu = !this.showMenu;
    this.toggleMenuEvent.emit({ event, column: 'table' });
  }

  closeMenu(): void {
    if (!this.showMenu) return;
    this.showMenu = false;
    this.closeMenuEvent.emit();
  }

  onMenuClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  removeFilter(filter: string, column: string): void {
    if (this.useExternalState) {
      this.externalRemoveFilter.emit({ field: column, value: filter });
      return;
    }

    this.studyFilterService.removeFilter(column, filter, 'table');
  }

  clearAllFilters(): void {
    if (this.useExternalState) {
      this.externalClearAll.emit();
      this.showMenu = false;
      this.closeMenuEvent.emit();
      return;
    }

    this.studyFilterService.clearAllFilters('table');
    this.showMenu = false;
    this.closeMenuEvent.emit();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeMenu();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeMenu();
  }

  trackByKey(_: number, item: FilterColumn): string {
    return item.key;
  }

  trackByFilter(_: number, item: string): string {
    return item;
  }

  private refreshFromService(): void {
    const applied = this.filtersApplied ?? {};
    this._filtersAppliedArray = this.toFilterColumns(applied);
    this._totalFiltersApplied = this.countTotal(applied);
  }

  private refreshFromExternalState(): void {
    const external = this.externalFiltersApplied ?? {};
    this._filtersAppliedArray = Object.entries(external).map(([key, value]) => ({
      key,
      value: Array.isArray(value) ? [...value] : []
    }));

    this._totalFiltersApplied =
      typeof this.externalTotalFiltersApplied === 'number'
        ? this.externalTotalFiltersApplied
        : this._filtersAppliedArray.reduce((sum, item) => sum + item.value.length, 0);
  }

  private toFilterColumns(filters: FilterList): FilterColumn[] {
    return Object.entries(filters ?? {}).map(([key, value]) => ({
      key,
      value: this.toStringArray(value)
    }));
  }

  private countTotal(filters: FilterList): number {
    let total = 0;

    for (const value of Object.values(filters ?? {})) {
      if (Array.isArray(value)) {
        total += value.length;
        continue;
      }

      if (value && typeof value === 'object') {
        const anyVal = value as any;

        if (typeof anyVal.size === 'number') {
          total += anyVal.size;
          continue;
        }

        if (typeof anyVal.values === 'function') {
          total += Array.from(anyVal.values()).length;
          continue;
        }
      }
    }

    return total;
  }

  private toStringArray(value: unknown): string[] {
    if (Array.isArray(value)) return value;

    if (value && typeof value === 'object') {
      const anyVal = value as any;
      if (typeof anyVal.values === 'function') {
        return Array.from(anyVal.values()) as string[];
      }
    }

    return [];
  }
}
